import { Navigate, createFileRoute } from '@tanstack/react-router'

import { useDashboardRole } from '@/components/dashboard/use-dashboard-role'
import { ManagerDashboard } from '@/components/dashboard/manager-dashboard'
import { SalesDashboard } from '@/components/dashboard/sales-dashboard'
import { WarehouseDashboard } from '@/components/dashboard/warehouse-dashboard'
import { DriverDashboard } from '@/components/dashboard/driver-dashboard'
import { FallbackDashboard } from '@/components/dashboard/fallback-dashboard'

export const Route = createFileRoute('/_app/_authed/_sidebar/')({
    // Optional (`?`) + undefined when unset: the router never writes
    // ?kioskExit=false back into the URL, and links to `/` don't have to
    // spell out a search object.
    validateSearch: (
        search: Record<string, unknown>,
    ): { kioskExit?: true } => ({
        kioskExit:
            search.kioskExit === true ||
            search.kioskExit === 'true' ||
            search.kioskExit === '1' ||
            undefined,
    }),
    component: DashboardPage,
})

/**
 * Home route dispatcher. Renders the dashboard matching the current user's
 * permission capabilities (see resolveDashboard for the tie-break order), or
 * redirects kiosk devices to their dedicated screen. Waits for permissions to
 * load before resolving so a privileged user never flashes the fallback.
 */
function DashboardPage() {
    const { kioskExit } = Route.useSearch()
    const resolution = useDashboardRole({ allowKioskRedirect: !kioskExit })

    if (resolution.kind === 'loading') {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="animate-spin h-8 w-8 border-2 border-muted-foreground border-t-transparent rounded-full" />
            </div>
        )
    }

    if (resolution.kind === 'redirect') {
        return <Navigate to={resolution.to} />
    }

    switch (resolution.dashboard) {
        case 'manager':
            return <ManagerDashboard />
        case 'sales':
            return <SalesDashboard />
        case 'warehouse':
            return <WarehouseDashboard />
        case 'driver':
            return <DriverDashboard />
        case 'fallback':
            return <FallbackDashboard />
    }
}
